import { DriftAccountsStore } from 'src/stores/useDriftAccountsStore';
import { MAIN_POOL_ID, UIMarket } from '@velocity-exchange/common';
import { ModalParams, CollateralModalType } from 'src/components/Modals/types';
import { NewSubaccountModalProps } from 'src/components/Modals/CollateralModal/types';

export default function processCollateralModal(
	{ props: params, type }: Extract<ModalParams, { type: CollateralModalType }>,
	accounts: DriftAccountsStore['accounts'],
	currentAccountKey: string | undefined,
	isPrivyNativeAuth: boolean
): ModalParams {
	let modalTypeToUse = type;

	let accountKeyToUse = params.targetAccountKey ?? currentAccountKey ?? null;
	const accountToUseData = accounts[accountKeyToUse ?? ''];
	const accountToUsePoolId = accountToUseData?.poolId;

	let bankIndexToUse = params.collateralIndex ?? 0;

	const bankToUsePoolId = UIMarket.spotMarkets.find(
		(m) => m.marketIndex === bankIndexToUse
	)?.poolId as number;

	let poolIdToUse;

	// Handle case where we're triggering a collateral modal but no current account exists
	if (!accountToUseData) {
		return {
			type: isPrivyNativeAuth ? 'deposit_from_other_sources' : 'deposit',
			props: {
				...params,
				collateralIndex:
					params.collateralIndex === undefined ? 0 : bankIndexToUse,
				collateralPoolId:
					params.collateralIndex === undefined ? MAIN_POOL_ID : bankToUsePoolId,
				targetAccountKey: null,
				defaultLargestBalance: params.defaultLargestBalance ?? null,
			},
		};
	}

	// For new_subaccount modal, don't force the account's poolId
	// User should be able to create accounts in any pool
	if (type === 'new_subaccount') {
		const newSubaccountParams = params as NewSubaccountModalProps;
		return {
			type: 'new_subaccount',
			props: {
				...newSubaccountParams,
				collateralIndex: bankIndexToUse,
				collateralPoolId: bankToUsePoolId,
				targetAccountKey: accountKeyToUse,
				defaultLargestBalance: params.defaultLargestBalance ?? null,
			},
		};
	}

	if (bankToUsePoolId !== accountToUsePoolId) {
		if (
			params.collateralIndex !== undefined &&
			params.targetAccountKey !== undefined
		) {
			// If both bank index and account key were specified and they don't have matching pool ids, something is wrong.
			throw new Error(
				'Intent bank index pool id does not match params account pool id'
			);
		}

		// By default prefer using the Pool ID for the account
		let accountLeads = true;

		if (params.collateralIndex !== undefined) {
			// If the bank index was specified, choose it instead of the account
			accountLeads = false;
		}

		if (accountLeads) {
			// Account leads, find the first bank index with the matching pool id for the account
			const bankIndexWithMatchingPoolId = UIMarket.spotMarkets.findIndex(
				(market) => market.poolId === accountToUsePoolId
			);
			bankIndexToUse = bankIndexWithMatchingPoolId;
			poolIdToUse = accountToUsePoolId;
		} else {
			// Bank index leads, find the account with the matching pool id for the bank
			const accountWithMatchingPoolId = Object.values(accounts).find(
				(account) => account?.poolId === bankToUsePoolId
			);

			if (!accountWithMatchingPoolId) {
				// If we don't have an account with the matching pool id, we need to force the new subaccount modal
				modalTypeToUse = 'deposit';
				accountKeyToUse = null;
			} else {
				accountKeyToUse = accountWithMatchingPoolId?.userKey;
			}

			poolIdToUse = bankToUsePoolId;
		}
	} else {
		poolIdToUse = accountToUsePoolId;
	}

	// Redirect withdraw and deposit modals when using Privy-native auth.
	if (isPrivyNativeAuth) {
		if (modalTypeToUse === 'deposit') {
			modalTypeToUse = 'deposit_from_other_sources';
		}

		if (modalTypeToUse === 'withdraw') {
			modalTypeToUse = 'withdraw_to_other_destinations';
		}
	}

	return {
		type: modalTypeToUse,
		props: {
			...params,
			collateralPoolId: poolIdToUse as number,
			collateralIndex: bankIndexToUse,
			targetAccountKey: accountKeyToUse,
			defaultLargestBalance: params.defaultLargestBalance ?? null,
		},
	};
}

const COLLATERAL_MODAL_TYPES: CollateralModalType[] = [
	'deposit',
	'withdraw',
	'borrow',
	'repay',
	'transfer',
	'new_subaccount',
	'deposit_from_other_sources',
	'withdraw_to_other_destinations',
];

export function isCollateralModal(
	modal: ModalParams
): modal is Extract<ModalParams, { type: CollateralModalType }> {
	// Have to use `some` here because type of `modal.type` is wider than `CollateralModalType`.
	return COLLATERAL_MODAL_TYPES.some((type) => type === modal.type);
}
